import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import ArticleEditor from '../../../components/editor/ArticleEditor';
import axiosPost from '../../../helpers/axios-helper';
import '../../dashboard/styles/Dashboard.css';

export default function TravelGuides() {
  const [entries, setEntries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorData, setEditorData] = useState(null);

  const [filters, setFilters] = useState({ country_id: '', active: '' });

  useEffect(() => {
    loadCountries();
    loadEntries();
  }, []);

  const loadCountries = () => {
    axiosPost(
      'dashboard/countries/list',
      { 'Content-Type': 'application/json' },
      { include_inactive: 1 },
      (resp) => {
        if (resp?.success) {
          const list = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
          setCountries(list);
        }
      },
      (err) => console.error('Failed to load countries:', err)
    );
  };

  const loadEntries = () => {
    setIsLoading(true);
    const payload = {};
    if (filters.country_id) payload.country_id = parseInt(filters.country_id);
    if (filters.active !== '') payload.active = parseInt(filters.active);

    axiosPost(
      'travel-guides/list',
      { 'Content-Type': 'application/json' },
      payload,
      (resp) => {
        if (resp?.success) {
          setEntries(resp.data || []);
        }
        setIsLoading(false);
      },
      (err) => {
        console.error('Failed to load entries:', err);
        setIsLoading(false);
      }
    );
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    loadEntries();
  };

  const handleAddNew = () => {
    setEditorData({
      id: null,
      title: '',
      excerpt: '',
      image: '',
      category: 'Travel Guide',
      country_id: '',
      tags: [],
      blocks: [],
      date_published: Math.floor(Date.now() / 1000),
      status: 'draft'
    });
    setIsEditorOpen(true);
  };

  const handleEdit = (entry) => {
    axiosPost(
      'travel-guides/get',
      { 'Content-Type': 'application/json' },
      { id: entry.id },
      (resp) => {
        if (resp?.success && resp.data) {
          const full = resp.data;
          setEditorData({
            id: full.id,
            title: full.title || `Plan your ${full.country_name || ''} Trip`,
            excerpt: full.excerpt || '',
            image: full.image || '',
            category: 'Travel Guide',
            country_id: full.country_id || '',
            tags: [],
            blocks: Array.isArray(full.blocks) ? full.blocks.map((b, idx) => ({
              id: `block-${full.id}-${idx}`,
              type: b.type,
              content: b.content
            })) : [],
            date_published: full.updated_at || Math.floor(Date.now() / 1000),
            status: full.status || (full.active ? 'published' : 'draft'),
            highlight_title: full.highlight_title || 'Highlights',
            highlight_items: full.highlight_items || '',
            notes_title: full.notes_title || 'Good to know',
            notes_items: full.notes_items || ''
          });
          setIsEditorOpen(true);
        } else {
          alert('Failed to load entry: ' + (resp?.response || 'Unknown error'));
        }
      },
      (err) => {
        console.error('Failed to load entry:', err);
        alert('Failed to load entry');
      }
    );
  };

  const handleEditorSave = (data) => {
    const payload = {
      id: data.id || 0,
      country_id: parseInt(data.country_id),
      title: data.title || '',
      excerpt: data.excerpt || '',
      image: data.image || '',
      status: data.status || 'draft',
      active: data.status === 'published' ? 1 : 0,
      highlight_title: data.highlight_title || '',
      highlight_items: data.highlight_items || '',
      notes_title: data.notes_title || '',
      notes_items: data.notes_items || '',
      blocks: data.blocks.map(block => ({
        type: block.type,
        content: block.content
      }))
    };

    axiosPost(
      'travel-guides/update',
      { 'Content-Type': 'application/json' },
      payload,
      (resp) => {
        if (resp?.success) {
          loadEntries();
          setIsEditorOpen(false);
          setEditorData(null);
          alert('Travel guide saved successfully!');
        } else {
          alert('Failed to save: ' + (resp?.response || 'Unknown error'));
        }
      },
      (err) => {
        console.error('Save failed:', err);
        alert('Failed to save entry');
      }
    );
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setEditorData(null);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    axiosPost(
      'travel-guides/delete',
      { 'Content-Type': 'application/json' },
      { id },
      (resp) => {
        if (resp?.success) {
          loadEntries();
          alert('Entry deleted successfully');
        } else {
          alert('Failed to delete: ' + (resp?.response || 'Unknown error'));
        }
      },
      (err) => {
        console.error('Delete failed:', err);
        alert('Failed to delete entry');
      }
    );
  };

  if (isEditorOpen && editorData) {
    return (
      <ArticleEditor
        initialData={editorData}
        onSave={handleEditorSave}
        onClose={handleEditorClose}
        mode="travel-guide"
      />
    );
  }

  return (
    <div>
      <div className="DashboardPanel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 className="DashboardTitle">Travel Guides Management</h1>
          <button className="DashboardButton" onClick={handleAddNew}>
            <FaPlus style={{ marginRight: '8px' }} />
            Add New Entry
          </button>
        </div>

        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <select name="country_id" value={filters.country_id} onChange={handleFilterChange} className="DashboardSelect">
                <option value="">All Countries</option>
                {countries.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              <select name="active" value={filters.active} onChange={handleFilterChange} className="DashboardSelect">
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>

              <button className="DashboardButton" onClick={handleApplyFilters}>
                Apply Filters
              </button>
            </div>

            {entries.length === 0 ? (
              <p>No entries found. Click "Add New Entry" to create one.</p>
            ) : (
              <table className="DashboardTable">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Country</th>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Last Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(entry => (
                    <tr key={entry.id}>
                      <td>{entry.id}</td>
                      <td>{entry.country_name || 'Unknown'}</td>
                      <td>{entry.title || `Travel Guide - ${entry.country_name}`}</td>
                      <td>
                        <span className={entry.active ? 'StatusActive' : 'StatusInactive'}>
                          {entry.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{entry.updated_at ? new Date(entry.updated_at * 1000).toLocaleDateString() : '-'}</td>
                      <td>
                        <button className="DashboardButtonSmall" onClick={() => handleEdit(entry)}>
                          <FaEdit /> Edit
                        </button>
                        <button className="DashboardButtonSmall DashboardButtonDanger" onClick={() => handleDelete(entry.id)}>
                          <FaTrash /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
